import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { useState } from 'react';
import * as React from 'react';

import { PropertyListing } from '@shared-ui/lodging-property-listing';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';

import { PaginationSubSets } from 'models';
import { PROPERTY_LISTING_BATCH_KEY } from 'src/common/constants/batchKeys';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLoader } from 'uitk-react-loader';

import useSecondaryFetchStatus from './use-secondary-fetch-status-hook';

export interface PropertyListingWrapperProps {
  context?: ContextStore;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  uiPage?: UIPageStore;
  currentHotel?: CurrentHotelStore;
  searchCriteria?: SearchCriteriaModel;
  analytics?: AnalyticsStore;
  searchInputs?: any;
  criteriaChanged?: boolean;
}

export const PropertyListingWrapper = inject(
  'context',
  'multiItem',
  'uiPage',
  'currentHotel',
  'searchCriteria',
  'searchResults',
  'experiment',
  'analytics'
)(
  observer(
    ({
      analytics,
      context,
      uiPage,
      multiItem,
      currentHotel,
      searchCriteria,
      searchResults,
      experiment,
      searchInputs,
      criteriaChanged,
    }: PropertyListingWrapperProps) => {
      const [inputs, setInputs] = useState(searchInputs);

      React.useEffect(() => {
        if (criteriaChanged) {
          setInputs(searchInputs);
        }
      }, [criteriaChanged]);

      if (!context || !searchCriteria || !searchResults?.searchCriteria) return null;
      const multiItemContext = multiItem?.multiItemContext || null;

      const showLoadingSpinner = useSecondaryFetchStatus({ searchResults, context, multiItem, uiPage, analytics, experiment });

      const openDatepicker = (elementId?: string) => {
        if (elementId === 'propertySelectDateCard') {
          const searchCriteriaInResults = searchResults?.searchCriteria;
          if (searchCriteriaInResults) {
            searchCriteriaInResults.dateSelectedByUser = true;
          }
        }
        uiPage?.dispatchOpenNewDatePickerEvent();
      };

      const batchKey = experiment?.exposures?.SHOPPING_PWA_BATCHING_SRP_QUERY?.bucket === 1 ? PROPERTY_LISTING_BATCH_KEY : undefined;

      // Temporarily passing the PWA search listings response to the shared UI component to avoid duplicating the
      // request for search listings on SRP. Once we migrate the listings fetch to shared UI this will be cleaned up.
      const useSharedUIListingsQuery =
        !multiItem?.isPackageShoppingPath() && experiment?.exposures?.EG_Property_Search_Query_Migration?.bucket === 1;
      const propertyListingControlInputs = () => {
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          context: bexApiContext,
          ...rest
        } = getPropertySearchInput(context, searchResults.searchCriteria, multiItemContext);

        return {
          ...rest,
          rooms: searchResults.searchCriteria.rooms,
        };
      };

      const searchListingsProps = useSharedUIListingsQuery
        ? {
            inputs: inputs,
          }
        : {
            inputs: propertyListingControlInputs(),
            propertySearchListingsPwa: searchResults.propertySearchListings as any,
            propertySearchListingAdaptexAnalyticsEvents: searchResults.propertyListingAdaptexAnalyticsSuccessEvents as any,
            isLoadingPwa: searchResults.isLoading,
          };

      return (
        <>
          <PropertyListing
            {...searchListingsProps}
            batchKey={batchKey}
            openDatePicker={openDatepicker}
            onClickCallback={(lodgingCard: { id: string }) => {
              currentHotel?.setPropertyListingAsCurrentHotel(lodgingCard);
            }}
            onHoverChanged={(id: string, hovered: boolean) => {
              searchCriteria.setHighlightedPropertyId(hovered ? id : '');
            }}
            onPaginationCallback={(paginationSubSets: PaginationSubSets) => {
              searchCriteria.paginationSubSets = paginationSubSets;
            }}
          />
          {!useSharedUIListingsQuery && showLoadingSpinner && (
            <UitkLayoutFlex justifyContent="center">
              <UitkLayoutFlexItem>
                <UitkLoader />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          )}
        </>
      );
    }
  )
);
